// Explore page API client — search entities, top-referenced, entity documents.

import { getCsrfToken } from './api';
import type { IocLookupDocument } from './graphApi';

export interface ExploreSearchResult {
  value: string;
  category: string;
  sub_type?: string | null;
  doc_count: number;
  extra?: Record<string, unknown> | null;
}

export interface ExploreSearchResponse {
  results: ExploreSearchResult[];
  total: number;
  query: string;
}

export interface ExploreSearchParams {
  query: string;
  category?: string | null;
  sort_by?: string;
  limit?: number;
  offset?: number;
  country?: string | null;
}

export interface TopReferencedCategory {
  category: string;
  items: ExploreSearchResult[];
}

export interface TopReferencedResponse {
  categories: TopReferencedCategory[];
  stats: Record<string, number>;
}

export interface EntityDocsParams {
  value: string;
  category: string;
  limit?: number;
  offset?: number;
}

export interface ExploreCategoryItem {
  key: string;
  label: string;
  count: number;
}

export interface ExploreCategoryGroup {
  key: string;
  label: string;
  categories: ExploreCategoryItem[];
}

export interface ExploreCategoriesResponse {
  groups: ExploreCategoryGroup[];
}

export interface EntityDocumentsResponse {
  value: string;
  category: string;
  documents: IocLookupDocument[];
  total: number;
}

export async function searchEntities(params: ExploreSearchParams): Promise<ExploreSearchResponse> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/explore/search', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
    body: JSON.stringify({
      query: params.query || '',
      ...(params.category ? { category: params.category } : {}),
      ...(params.country ? { country: params.country } : {}),
      sort_by: params.sort_by || 'doc_count',
      limit: params.limit || 50,
      offset: params.offset || 0,
    }),
  });

  if (!resp.ok) return { results: [], total: 0, query: params.query };
  return (await resp.json()) as ExploreSearchResponse;
}

export async function fetchExploreCategories(): Promise<ExploreCategoriesResponse> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/explore/categories', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'X-CSRFToken': csrf },
  });
  if (!resp.ok) return { groups: [] };
  return (await resp.json()) as ExploreCategoriesResponse;
}

export async function fetchTopReferenced(): Promise<TopReferencedResponse> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/explore/top-referenced', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'X-CSRFToken': csrf },
  });

  if (!resp.ok) return { categories: [], stats: {} };
  return (await resp.json()) as TopReferencedResponse;
}

export async function fetchEntityDocuments(params: EntityDocsParams): Promise<EntityDocumentsResponse> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/explore/entity-documents', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
    body: JSON.stringify({
      value: params.value,
      category: params.category,
      limit: params.limit || 50,
      offset: params.offset || 0,
    }),
  });

  if (!resp.ok) return { value: params.value, category: params.category, documents: [], total: 0 };
  return (await resp.json()) as EntityDocumentsResponse;
}
